import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { CANMAP_CUSTOMER_ENDPOINT, CANMAP_ENDPOINT, CANMAP_TYPE_GET_SUFFIX } from '../config/config.constants';
import { CanMapDocument } from 'src/app/pages/UControl/config/models/can-map-block.model';

export interface CanMapEntity {
  idCanMapType: number;
  uidCanMapType: string;
  description: string;
  /** Contenuto JSON serializzato: fare parseCanMapDocument() per ottenere il documento navigabile */
  jsonContent: string;
  MD5: string;
  creationDate: number;
  modifiedDate?: number;
  version: number;
}

/**
 * Chiamate HTTP verso CanMapsV2Controller per la lettura/scrittura del contenuto
 * JSON di una CAN Map, identificata dal suo uidCanMapType.
 *
 * Nota: la GET .../canmaptype non ha precedenti di utilizzo da nessun altro flusso
 * frontend (pages/can-map usa invece l'endpoint di lista ofCustomer). Va collaudata
 * esplicitamente prima di considerarla affidabile in produzione.
 *
 * Per la scrittura si riusa deliberatamente l'endpoint "new-version/of" già collaudato
 * da import-map-modal.component.ts (stesso endpoint usato per il caricamento di un
 * nuovo file di CAN Map): l'endpoint PATCH .../updatemaptype è stato scartato perché
 * ha un bug di binding lato backend (nome del parametro C# non corrisponde al nome
 * del segmento di route) ed è comunque privo di precedenti d'uso in produzione.
 */
@Injectable({ providedIn: 'root' })
export class CanMapV2Service {
  constructor(private apiService: ApiService) {}

  getCanMapType(uidCanMapType: string): Observable<CanMapEntity> {
    return this.apiService.get(CANMAP_ENDPOINT + uidCanMapType + CANMAP_TYPE_GET_SUFFIX).pipe(
      map((response: any) => response.data as CanMapEntity)
    );
  }

  /**
   * Crea una nuova versione della CAN Map a partire dal documento mergiato, tramite
   * lo stesso endpoint multipart già usato dal flusso di import manuale di un file.
   * Il documento viene serializzato in un Blob JSON e inviato come se fosse un file caricato.
   */
  createNewCanMapVersion(uidCustomer: string, uidCanMapType: string, canMapDocument: CanMapDocument): Observable<any> {
    const jsonBlob = new Blob([JSON.stringify(canMapDocument)], { type: 'application/json' });
    const formData = new FormData();
    formData.append('', jsonBlob, 'canmap.json');
    return this.apiService.post(CANMAP_CUSTOMER_ENDPOINT + uidCustomer + '/new-version/of/' + uidCanMapType, formData);
  }

  /**
   * Effettua il parse difensivo di jsonContent. Ritorna null (invece di lanciare)
   * se il contenuto non è un JSON valido, per permettere ai chiamanti un fail-open
   * (mostrare comunque il solo pannello "nuovo blocco" senza bloccare l'utente).
   */
  parseCanMapDocument(entity: CanMapEntity): CanMapDocument | null {
    try {
      const parsed = JSON.parse(entity.jsonContent);
      if (!parsed || !Array.isArray(parsed.data)) {
        return null;
      }
      return parsed as CanMapDocument;
    } catch {
      return null;
    }
  }
}
